import { Transaction } from '../types/transaction.types';
import { CartItem } from '../types/book.types';
import { TAX_RATE } from './SalesService';
import { getWarehouseId } from './TerminalService';
import { TicketModel } from '../utils/escpos';
import { getThermalConfig, printTicketRaw } from './ThermalPrinterService';
import { AVISO_PLAZO_TICKET } from '../constants/returns.constants';

/**
 * Impresion del ticket en la termica de 80mm.
 *
 * El navegador NO puede elegir impresora: window.print() manda siempre a la
 * predeterminada de Windows. Que el ticket salga por la termica, y que salga
 * sin que el cajero toque nada, depende de la configuracion de la caja y no de
 * este codigo:
 *
 *   - Chrome tiene que arrancar con --kiosk-printing, que es lo que quita el
 *     cuadro de impresion. OJO: --kiosk a secas solo pone pantalla completa y
 *     el cuadro sigue saliendo.
 *   - La termica tiene que ser la predeterminada, y hay que desactivar
 *     "Permitir que Windows administre mi impresora predeterminada" o la
 *     ultima impresora usada le roba el destino.
 *   - El tamano de rollo tiene que estar puesto como valor por defecto de la
 *     cola: sin cuadro de impresion no hay donde elegirlo, y con un tamano de
 *     folio el ticket sale arrinconado en una hoja A4.
 *
 * Todo eso lo deja hecho tpv-setup/configurar-caja.ps1.
 *
 * Y una limitacion que no tiene arreglo por esta via: el navegador no devuelve
 * ninguna señal de exito, asi que la aplicacion no puede saber si el papel
 * llego a salir. Solo se detecta no haber podido MANDAR el documento.
 */

/**
 * Datos del centro. En el ticket real van en cabecera y cambian de una libreria
 * a otra (el sufijo "-DCHO-ECO" es el centro), asi que no vale una sola
 * direccion para toda la flota. Lo correcto es que los sirva el backend junto
 * al almacen; hasta entonces, se mapean aqui por warehouse_id.
 *
 * OJO: solo esta comprobado el centro de Barbara de Braganza. Falta saber que
 * warehouse_id le corresponde y anadir el resto de librerias.
 */
export interface DatosCentro {
    razonSocial: string;
    nif: string;
    direccion: string;
    poblacion: string;
    telefono: string;
    email: string;
}

const CENTRO_POR_DEFECTO: DatosCentro = {
    razonSocial: 'MARCIAL PONS LIBRERO SL  -DCHO-ECO',
    nif: 'ESB82947326',
    direccion: 'BARBARA DE BRAGANZA, 11,',
    poblacion: '28004 MADRID',
    telefono: 'Tel:913194250',
    email: 'economia@marcialpons.es'
};

const CENTROS: { [warehouseId: string]: DatosCentro } = {
    // '1': { ... }  <-- rellenar con cada libreria y su warehouse_id
};

export const centroDe = (warehouseId: string): DatosCentro => CENTROS[warehouseId] || CENTRO_POR_DEFECTO;

// El plazo sale de returns.constants: es el mismo que corta las devoluciones.
const PIE_LEGAL = [
    AVISO_PLAZO_TICKET,
    '* No se admiten devoluciones en libros de 2ª mano.'
];

const EUROS = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (valor: number): string => EUROS.format(valor);

const redondear = (valor: number): number => Math.round(valor * 100) / 100;

/** Los titulos vienen del backend: hay que escaparlos antes de meterlos en el HTML. */
const esc = (texto: any): string =>
    String(texto === undefined || texto === null ? '' : texto).replace(
        /[&<>"']/g,
        c => (({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as any)[c])
    );

/**
 * El cuerpo del ticket va SIN IVA, como el ticket de papel: la linea muestra el
 * PVP sin impuesto y antes de descuento, y el descuento baja aparte. El PVP del
 * catalogo lleva el IVA dentro, asi que se le quita aqui.
 */
const brutoSinIva = (item: CartItem): number =>
    (item.price * item.quantity) / (1 + TAX_RATE / 100);

/**
 * Misma zona con la que ticket_list convierte created_at. Con la fecha local
 * del navegador, un equipo en otra zona imprimiria un dia que no cuadra con el
 * historial. El ticket de papel la lleva con puntos.
 */
const fechaCorta = (fecha: Date): string =>
    fecha.toLocaleDateString('es-ES', {
        timeZone: 'Europe/Madrid',
        day: '2-digit', month: '2-digit', year: 'numeric'
    }).replace(/\//g, '.');

const filaLinea = (linea: { texto: string; importe: number }): string => `
      <div class="fila">
        <span class="concepto">${esc(linea.texto)}</span>
        <span>${money(linea.importe)}</span>
      </div>`;

/**
 * Todo lo que lleva el ticket, ya calculado. Lo comparten el ticket HTML (que
 * sale por la impresora del navegador) y el ESC/POS (que va directo a la
 * termica): asi los dos documentos no pueden diferir.
 */
export const modeloTicket = (tx: Transaction): TicketModel => {
    const centro = centroDe(getWarehouseId());
    const esDevolucion = tx.type === 'return';

    // Base = lo que guarda tickets_create como subtotal, y es la "Base" del
    // ticket de papel. El descuento sale de la diferencia con la suma de lineas,
    // asi absorbe tanto los descuentos de linea como la promocion global sin
    // tener que reconstruir el desglose (que llega distinto segun el ticket
    // venga del carrito o del backend).
    //
    // Se cuadra sobre importes YA redondeados a centimos, no al final: en un
    // documento de cobro la columna tiene que sumar exactamente lo que se lee.
    // Redondeando al final, Suma - Descuento se queda a un centimo de la Base
    // y el ticket no cuadra.
    const suma = redondear(tx.items.reduce((acumulado, item) => acumulado + redondear(brutoSinIva(item)), 0));
    const base = redondear(tx.total / (1 + TAX_RATE / 100));
    const descuento = redondear(suma - base);
    const iva = redondear(tx.total - base);

    // Una devolucion es una rectificativa: todos los importes en negativo. En el
    // ticket llegan en positivo (tx.total es el valor absoluto), asi que el signo
    // se pone aqui, una sola vez, para que lo hereden el HTML y el ESC/POS.
    const firmar = (valor: number): number => (valor === 0 ? 0 : esDevolucion ? -valor : valor);

    return {
        centro,
        titulo: esDevolucion ? 'Fra. Simplificada Rectificativa' : 'Fra. Simplificada',
        esDevolucion,
        fecha: fechaCorta(tx.timestamp),
        numero: String(tx.ticketNumber || tx.id),
        // Con la serie ("T1-1"). El id interno solo vale si no se conoce el numero.
        sobreTicket: String(tx.refundsTicketNumber || tx.refundsTicketId || ''),
        lineas: tx.items.map(item => ({
            texto: `${item.quantity} ${String(item.title).toUpperCase()}`,
            importe: firmar(redondear(brutoSinIva(item)))
        })),
        suma: firmar(suma),
        descuento,
        base: firmar(base),
        ivaPct: TAX_RATE,
        iva: firmar(iva),
        total: firmar(tx.total),
        exentoIva: !!tx.isTaxFree,
        pieLegal: PIE_LEGAL
    };
};

const construirHtml = (tx: Transaction): string => {
    const m = modeloTicket(tx);

    return `<!doctype html>
<html lang="es"><head><meta charset="utf-8"><title>Ticket</title><style>
  /* 72mm y no 80: el driver declara el papel como "80(72.1)", o sea rollo de
     80mm con 72,07mm imprimibles, y el tamano de pagina que expone Windows es
     ese, 72,07. Pidiendo 80 aqui, Chrome maqueta una pagina mas ancha que el
     medio y la encaja desplazada: margen a la izquierda y recorte a la derecha. */
  @page { size: 72mm auto; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000; }
  /* El papel mide 80mm pero el area imprimible del driver son 72,07mm, pegada
     al borde izquierdo: lo que pase de ahi se recorta. Por eso el ticket mide
     72mm EN TOTAL (border-box, con el padding dentro) y no 72 + padding. */
  .ticket { width: 72mm; box-sizing: border-box; padding: 3mm; font-family: "Arial", "Helvetica", sans-serif; font-size: 11px; line-height: 1.45; }
  .centro { text-align: center; }
  .aviso { font-weight: bold; }
  .hueco { height: 3mm; }
  .regla { border-top: 1px solid #000; margin: 2mm 0; }
  .fila { display: flex; justify-content: space-between; gap: 3mm; }
  .fila > span:last-child { white-space: nowrap; }
  /* El titulo se recorta en vez de partirse, como en el ticket de papel. */
  .concepto { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .legal { font-size: 10px; line-height: 1.3; }
</style></head><body><div class="ticket">

  <div>${esc(m.centro.razonSocial)}</div>
  <div>${esc(m.centro.nif)}</div>
  <div>${esc(m.centro.direccion)}</div>
  <div>${esc(m.centro.poblacion)}</div>
  <div>${esc(m.centro.telefono)}</div>
  <div>${esc(m.centro.email)}</div>

  <div class="hueco"></div>

  <div>${esc(m.titulo)}</div>
  <div>FECHA: ${esc(m.fecha)}&nbsp;&nbsp;Nº ${esc(m.numero)}</div>
  ${m.sobreTicket ? `<div>Sobre ticket: ${esc(m.sobreTicket)}</div>` : ''}

  <div class="hueco"></div>
  <div class="hueco"></div>

  ${m.lineas.map(filaLinea).join('')}

  <div class="hueco"></div>

  <div class="fila"><span>Suma:</span><span>${money(m.suma)}</span></div>
  ${m.descuento > 0.005 ? `<div class="fila"><span>Descuento:</span><span>${money(m.descuento)}${m.esDevolucion ? '' : '-'}</span></div>` : ''}

  <div class="hueco"></div>

  <div class="fila"><span>Base:</span><span>${money(m.base)}</span></div>
  <div class="fila"><span>IVA ${m.ivaPct}%:</span><span>${money(m.iva)}</span></div>

  <div class="hueco"></div>

  <div class="fila"><span>TOTAL (euros):</span><span>${money(m.total)}</span></div>
  ${m.exentoIva ? '<div class="centro" style="margin-top:2mm;">Operación exenta de IVA</div>' : ''}

  <div class="hueco"></div>
  <div class="regla"></div>

  <div class="centro aviso">*** GRACIAS POR SU VISITA ***</div>
  <div class="hueco"></div>
  <div class="centro">www.marcialpons.com</div>

  <div class="regla"></div>

  <div class="legal">
    ${m.pieLegal.map(linea => `<div>${esc(linea)}</div>`).join('')}
  </div>

</div></body></html>`;
};

/**
 * Manda el ticket a la impresora predeterminada.
 *
 * Se imprime desde un iframe propio y no con window.print() sobre la pagina del
 * TPV: asi el documento que sale es solo el ticket. Con window.print() habria
 * que esconder toda la aplicacion con reglas @media print, que es fragil y pelea
 * con el CSS de los componentes. Tampoco se usa window.open porque lo bloquean
 * los bloqueadores de ventanas y porque abre una ventana visible en la caja.
 */
const imprimirPorNavegador = (tx: Transaction): Promise<void> =>
    new Promise<void>((resolve, reject) => {
        if (!tx.items || tx.items.length === 0) {
            reject(new Error('El ticket no tiene líneas que imprimir.'));
            return;
        }

        const marco = document.createElement('iframe');
        // Fuera de pantalla pero RENDERIZADO: con display:none Chrome no tiene
        // nada que imprimir.
        marco.setAttribute('aria-hidden', 'true');
        marco.style.position = 'fixed';
        marco.style.left = '-10000px';
        marco.style.top = '0';
        marco.style.width = '72mm';
        marco.style.height = '200mm';
        marco.style.border = '0';

        let terminado = false;
        const retirar = () => {
            if (terminado) return;
            terminado = true;
            // Un respiro antes de quitarlo: Chrome lee el documento de forma
            // asincrona y arrancarlo demasiado pronto cancela el trabajo.
            setTimeout(() => {
                if (marco.parentNode) marco.parentNode.removeChild(marco);
            }, 2000);
        };

        marco.onload = () => {
            try {
                const ventana = marco.contentWindow;
                if (!ventana) throw new Error('El navegador no dejó crear el documento de impresión.');
                ventana.focus();
                ventana.print();
                retirar();
                resolve();
            } catch (err) {
                retirar();
                reject(err);
            }
        };

        marco.srcdoc = construirHtml(tx);
        document.body.appendChild(marco);
    });

/**
 * Imprime el ticket. Si en Ajustes hay una impresora termica elegida (USB o
 * serie) el ticket va directo a ella, en ESC/POS y sin dialogo. Si no la hay, o
 * si falla (desconectada, apagada), sale por el navegador como siempre: un
 * ticket no se pierde por un cable. `onFallback` recibe el motivo para avisar.
 */
export const printTicket = async (
    tx: Transaction,
    options: { onFallback?: (motivo: string) => void } = {}
): Promise<void> => {
    if (!tx.items || tx.items.length === 0) {
        throw new Error('El ticket no tiene líneas que imprimir.');
    }

    const config = getThermalConfig();
    if (config.mode !== 'browser') {
        try {
            await printTicketRaw(modeloTicket(tx), config);
            return;
        } catch (err) {
            if (options.onFallback) options.onFallback((err as any)?.message || 'No se pudo usar la impresora térmica.');
        }
    }

    await imprimirPorNavegador(tx);
};

/**
 * Ticket de prueba para Ajustes. Con termica elegida NO cae al navegador si
 * falla: el error tiene que verse, que es para lo que se prueba.
 */
export const printTestTicket = async (): Promise<void> => {
    const tx: any = {
        id: 'PRUEBA',
        ticketNumber: 'PRUEBA',
        timestamp: new Date(),
        items: [{
            id: '0', title: 'Prueba de impresión: ñ á é í ó ú € ¿?', author: '', price: 10.4, weight: 0, isbn: '', category: '',
            stock: 0, quantity: 1, discount: 0
        }],
        total: 10.4,
        type: 'sale',
        paymentMethod: 'cash'
    };

    const config = getThermalConfig();
    if (config.mode !== 'browser') {
        await printTicketRaw(modeloTicket(tx), config);
        return;
    }
    await imprimirPorNavegador(tx);
};

/** El mismo HTML que se manda a imprimir, para enseñarlo en pantalla (visor de documentos). */
export const htmlTicket = (tx: Transaction): string => construirHtml(tx);

